import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import useCartStore from "../api/cart";
import {
  CROTCH_NOTE,
  CUSTOM_KINDS,
  HOLE_PLACES,
  MAX_COUNT,
  MAX_CUSTOM,
  MAX_HOLES,
  MAX_NOTE,
  REBUILD_KEY,
  REINFORCE,
  SOMEWHERE_ELSE,
  WORN_THIN_ASK,
  asksWornThin,
  becomesCrotch,
  chargedHoles,
  chosenCustoms,
  crotchOn,
  extraLabel,
  holeSizeLabel,
  isCountable,
  jeansBasket,
  jeansQuote,
  missingForContinue,
  newCustom,
  newHole,
  offeredOn,
  priceOn,
  splitJeansServices,
} from "../utils/jeansScreen";

const pathURL = process.env.REACT_APP_API_URL;

const money = (amount) =>
  `$${Number.isInteger(amount) ? amount : Number(amount).toFixed(2)}`;

// One screen for every repair a pair of jeans needs, in place of the service
// and repair-detail screens. Alterations keep the old path: staff reach this
// by picking Repairing on the service screen. See utils/jeansScreen.js.
function JeansScreen() {
  const navigate = useNavigate();
  const routeState = useLocation()?.state;
  const { cartItems, updateCartItem } = useCartStore();

  const garment = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("selectedItems"));
    } catch {
      return null;
    }
  })();

  // The plan was picked on the plan screen, or it is already on the garment
  // when work is being added to an order. This screen shows it rather than
  // asking a second time.
  const plan =
    (routeState?.selectedPlan ||
      routeState?.selectedItem?.pricing_plan ||
      garment?.pricing_plan ||
      sessionStorage.getItem("SelectedPlan")) === "Premium"
      ? "Premium"
      : "Standard";

  const { data, isLoading } = useQuery({
    queryKey: ["jeansServices"],
    queryFn: async () => {
      const { data } = await axios.post(`${pathURL}service/getservicebyitem`, {
        text: "JEANS",
      });
      return Array.isArray(data) ? data : data?.data;
    },
  });
  const catalogue = useMemo(() => splitJeansServices(data), [data]);

  const [holes, setHoles] = useState([]);
  const [crotchTicked, setCrotchTicked] = useState(false);
  const [crotchMethod, setCrotchMethod] = useState(null);
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState("");
  const [customs, setCustoms] = useState([]);
  const [tried, setTried] = useState(false);

  const setHole = (index, change) =>
    setHoles((prev) =>
      prev.map((hole, i) => (i === index ? { ...hole, ...change } : hole)),
    );
  const setCount = (id, n) =>
    setCounts((prev) => ({
      ...prev,
      [id]: Math.max(0, Math.min(MAX_COUNT, n)),
    }));
  const setCustom = (index, change) =>
    setCustoms((prev) =>
      prev.map((custom, i) =>
        i === index ? { ...custom, ...change } : custom,
      ),
    );

  useEffect(() => {
    if (!cartItems?.[0] || !garment?.id) navigate("/");
  }, []);

  const extraOptions = catalogue.extras.filter((s) => offeredOn(s, plan));
  const wantsCrotch = crotchOn(holes, crotchTicked);
  const args = {
    garmentId: garment?.id,
    plan,
    holes,
    catalogue,
    crotchTicked,
    crotchMethod,
    counts,
    notes,
    customs,
  };
  const quote = jeansQuote(cartItems?.[0], args);
  // Each hole's own price on its row. The hole lines come first in the quote,
  // biggest first, so they line up with chargedHoles; a hole that turned into
  // crotch work is not in either.
  const charged = chargedHoles(holes, catalogue);
  const holePrice = {};
  charged.forEach((hole, at) => {
    holePrice[hole.index] = quote.prices[at];
  });
  const missing = missingForContinue(args);

  const firstGap = () => {
    const unplaced = holes.findIndex(
      (hole) =>
        !(hole.place === SOMEWHERE_ELSE ? hole.other.trim() : hole.place),
    );
    if (unplaced !== -1) {
      return document.getElementById(
        holes[unplaced]?.place
          ? `jeans-hole-other-${unplaced}`
          : `jeans-hole-place-${unplaced}`,
      );
    }
    const unasked = holes.findIndex((hole) => asksWornThin(hole) && !hole.thin);
    if (unasked !== -1) {
      return document.querySelector(`#jeans-hole-thin-${unasked} button`);
    }
    if (wantsCrotch && !crotchMethod) {
      return document.querySelector("#jeans-crotch-method button");
    }
    const custom = customs.findIndex(
      (c) =>
        (c.what.trim() || c.price) && (!c.what.trim() || !parseFloat(c.price)),
    );
    if (custom !== -1) {
      return document.getElementById(
        customs[custom].what.trim()
          ? `jeans-custom-price-${custom}`
          : `jeans-custom-what-${custom}`,
      );
    }
    return document.querySelector(".sweater-hole");
  };

  const handleContinue = () => {
    if (missing.length) {
      setTried(true);
      const gap = firstGap();
      gap?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (gap?.focus) gap.focus({ preventScroll: true });
      return;
    }
    updateCartItem(cartItems[0].id, jeansBasket(cartItems[0], args));
    navigate("/item-detail");
  };

  return (
    <section className="items-section same-bg-section sweater-page relative">
      <div className="container padding-x-all">
        <div className="items-box sec-com-padding flex-box justify-between sweater-screen">
          <div className="show-box mobile-none sweater-summary">
            <div className="show-box-list">
              <p className="show-box-para">This item</p>
              <h4 className="show-box-answer">{garment?.name || "JEANS"}</h4>
            </div>
            <div className="show-box-list">
              <p className="show-box-para">Plan</p>
              <h4 className="show-box-answer">{plan}</h4>
            </div>
            <div className="show-box-list">
              <p className="show-box-para">Repairs</p>
              <h4 className="show-box-answer">{money(quote.total)}</h4>
            </div>
          </div>
          <div className="item-select-box">
            <h2>What needs repairing?</h2>
            <p className="sweater-note">
              Everything that needs fixing on this pair, in one go. Hems, the
              waist and tapering are on the previous screen, under Alteration.
            </p>

            <div className="sweater-block">
              <h3 className="sweater-h">Holes and rips</h3>
              <p className="sweater-note">
                Measure each one at its widest point. The biggest is charged as
                the first hole and the rest at the each-extra price of their own
                size.
              </p>
              {isLoading && <p className="sweater-note">Loading prices…</p>}
              {holes.map((hole, index) => (
                <div className="sweater-hole" key={index}>
                  <span className="sweater-hole-n">Hole {index + 1}</span>
                  <select
                    id={`jeans-hole-size-${index}`}
                    aria-label={`Size of hole ${index + 1}`}
                    value={hole.size}
                    onChange={(e) => setHole(index, { size: +e.target.value })}
                  >
                    {catalogue.holes.map((row, i) => (
                      <option key={row.service._id} value={i}>
                        {holeSizeLabel(row.service)}
                      </option>
                    ))}
                  </select>
                  <span className="sweater-price">
                    {holePrice[index] === undefined
                      ? ""
                      : money(holePrice[index])}
                  </span>
                  <button
                    type="button"
                    className="sweater-remove"
                    aria-label={`Remove hole ${index + 1}`}
                    onClick={() =>
                      setHoles((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </button>
                  <select
                    id={`jeans-hole-place-${index}`}
                    aria-label={`Where hole ${index + 1} is`}
                    className={
                      hole.place
                        ? "sweater-where"
                        : tried
                          ? "sweater-where empty sweater-gap"
                          : "sweater-where empty"
                    }
                    value={hole.place}
                    onChange={(e) =>
                      setHole(index, { place: e.target.value, thin: null })
                    }
                  >
                    <option value="">Where is it?</option>
                    {HOLE_PLACES.map((place) => (
                      <option key={place}>{place}</option>
                    ))}
                    <option>{SOMEWHERE_ELSE}</option>
                  </select>
                  {hole.place === SOMEWHERE_ELSE && (
                    <input
                      id={`jeans-hole-other-${index}`}
                      type="text"
                      className={
                        tried && !hole.other.trim()
                          ? "sweater-where sweater-other sweater-gap"
                          : "sweater-where sweater-other"
                      }
                      maxLength={80}
                      placeholder="Where is it? e.g. below the back belt loop"
                      aria-label={`Describe where hole ${index + 1} is`}
                      value={hole.other}
                      onChange={(e) =>
                        setHole(index, { other: e.target.value })
                      }
                    />
                  )}
                  {asksWornThin(hole) && (
                    <>
                      <p className="sweater-method-note">{WORN_THIN_ASK}</p>
                      <div
                        id={`jeans-hole-thin-${index}`}
                        className={
                          tried && !hole.thin
                            ? "sweater-method sweater-gap"
                            : "sweater-method"
                        }
                        role="radiogroup"
                        aria-label={`Is the fabric around hole ${
                          index + 1
                        } worn thin?`}
                      >
                        {[
                          { key: "no", label: "No, the fabric is sound" },
                          { key: "yes", label: "Yes, it is worn thin" },
                        ].map(({ key, label }) => (
                          <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={hole.thin === key}
                            className={hole.thin === key ? "on" : ""}
                            onClick={() => setHole(index, { thin: key })}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {becomesCrotch(hole) && (
                        <p className="sweater-method-note" role="status">
                          Charged as crotch work below, not as a hole.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
              {holes.length < MAX_HOLES && (
                <button
                  type="button"
                  className="sweater-add"
                  onClick={() => setHoles((prev) => [...prev, newHole()])}
                >
                  {holes.length ? "+ Add another hole" : "+ Add a hole"}
                </button>
              )}
            </div>

            {(catalogue.crotch.reinforce || catalogue.crotch.rebuild) && (
              <div className="sweater-block">
                <h3 className="sweater-h">The crotch</h3>
                <div className="sweater-extra">
                  <label htmlFor="jeans-crotch">
                    <input
                      id="jeans-crotch"
                      type="checkbox"
                      checked={wantsCrotch}
                      disabled={holes.some(becomesCrotch)}
                      onChange={(e) => setCrotchTicked(e.target.checked)}
                    />
                    It is wearing through
                  </label>
                </div>
                {wantsCrotch && (
                  <>
                    <div
                      id="jeans-crotch-method"
                      className={
                        tried && !crotchMethod
                          ? "sweater-method sweater-gap"
                          : "sweater-method"
                      }
                      role="radiogroup"
                      aria-label="How we fix the crotch"
                    >
                      {[
                        { key: REINFORCE, service: catalogue.crotch.reinforce },
                        { key: REBUILD_KEY, service: catalogue.crotch.rebuild },
                      ]
                        .filter((choice) => choice.service)
                        .map(({ key, service }) => (
                          <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={crotchMethod === key}
                            className={crotchMethod === key ? "on" : ""}
                            onClick={() => setCrotchMethod(key)}
                          >
                            {key === REINFORCE ? "Reinforce" : "Full rebuild"}{" "}
                            {money(priceOn(service, plan))}
                          </button>
                        ))}
                    </div>
                    <p className="sweater-note">{CROTCH_NOTE}</p>
                  </>
                )}
              </div>
            )}

            {extraOptions.length > 0 && (
              <div className="sweater-block">
                <h3 className="sweater-h">Anything else?</h3>
                {extraOptions.map((service) => {
                  const n = counts[service._id] || 0;
                  return (
                    <div className="sweater-extra" key={service._id}>
                      <label htmlFor={`jeans-extra-${service._id}`}>
                        <input
                          id={`jeans-extra-${service._id}`}
                          type="checkbox"
                          checked={n > 0}
                          onChange={(e) =>
                            setCount(service._id, e.target.checked ? 1 : 0)
                          }
                        />
                        {extraLabel(service)}
                      </label>
                      <span className="sweater-extra-right">
                        {isCountable(service) && n > 0 && (
                          <span className="sweater-count">
                            <button
                              type="button"
                              aria-label={`One fewer: ${extraLabel(service)}`}
                              onClick={() => setCount(service._id, n - 1)}
                            >
                              −
                            </button>
                            <span aria-live="polite">{n}</span>
                            <button
                              type="button"
                              aria-label={`One more: ${extraLabel(service)}`}
                              disabled={n >= MAX_COUNT}
                              onClick={() => setCount(service._id, n + 1)}
                            >
                              +
                            </button>
                          </span>
                        )}
                        <span className="sweater-price">
                          {money(priceOn(service, plan) * Math.max(1, n))}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sweater-block">
              <h3 className="sweater-h">Custom work</h3>
              <p className="sweater-note">
                Not on the list, or charged by time. Say what we are doing and
                type the price.
              </p>
              {customs.map((custom, index) => (
                <div className="sweater-hole sweater-custom" key={index}>
                  <span className="sweater-hole-n">Custom {index + 1}</span>
                  <input
                    id={`jeans-custom-what-${index}`}
                    type="text"
                    maxLength={120}
                    placeholder={
                      custom.kind === "alteration"
                        ? "e.g. Move the back pockets"
                        : "e.g. Rebuild the belt loops, 1 hour"
                    }
                    aria-label={`What custom work ${index + 1} is`}
                    className={
                      tried && custom.price && !custom.what.trim()
                        ? "sweater-gap"
                        : ""
                    }
                    value={custom.what}
                    onChange={(e) => setCustom(index, { what: e.target.value })}
                  />
                  <span className="sweater-custom-price">
                    <span>$</span>
                    <input
                      id={`jeans-custom-price-${index}`}
                      type="number"
                      min="0"
                      step="1"
                      inputMode="decimal"
                      placeholder="0"
                      aria-label={`Price of custom work ${index + 1}`}
                      className={
                        tried && custom.what.trim() && !parseFloat(custom.price)
                          ? "sweater-gap"
                          : ""
                      }
                      value={custom.price}
                      onChange={(e) =>
                        setCustom(index, { price: e.target.value })
                      }
                    />
                  </span>
                  <button
                    type="button"
                    className="sweater-remove"
                    aria-label={`Remove custom work ${index + 1}`}
                    onClick={() =>
                      setCustoms((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </button>
                  <div
                    className="sweater-method"
                    role="radiogroup"
                    aria-label={`Repair or alteration, custom work ${index + 1}`}
                  >
                    {CUSTOM_KINDS.map(({ kind, label }) => (
                      <button
                        key={kind}
                        type="button"
                        role="radio"
                        aria-checked={custom.kind === kind}
                        className={custom.kind === kind ? "on" : ""}
                        onClick={() => setCustom(index, { kind })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {customs.length < MAX_CUSTOM && (
                <button
                  type="button"
                  className="sweater-add"
                  onClick={() => setCustoms((prev) => [...prev, newCustom()])}
                >
                  {chosenCustoms(customs).length
                    ? "+ Add more custom work"
                    : "+ Add custom work"}
                </button>
              )}
            </div>

            <div className="sweater-block">
              <h3 className="sweater-h">
                <label htmlFor="jeans-notes">
                  Anything else for our fixers?
                </label>{" "}
                <span className="item-field-optional">(optional)</span>
              </h3>
              <textarea
                id="jeans-notes"
                className="sweater-notes"
                placeholder="e.g. Patch from the inside, and keep the fading."
                value={notes}
                maxLength={MAX_NOTE}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="sweater-block">
              <div className="sweater-total">
                <span>Repairs</span>
                <span>{money(quote.total)}</span>
              </div>
            </div>

            <button
              className="section-main-btn sweater-continue"
              type="button"
              onClick={handleContinue}
            >
              Continue{" "}
              <span>
                <img src={btnArrow} alt="" />
              </span>
            </button>
            {tried && missing.length > 0 && (
              <p className="sweater-missing" role="alert">
                To continue, {missing.join(" and ")}.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default JeansScreen;
